/**
 * Reads that must come back whole: the paged read, and the guard for a single read.
 *
 * THE CEILING IS THE PROJECT'S API **Max rows** SETTING (1,000 on production; the owner keeps it
 * there, 2026-10-17). PostgREST clips every table read and every set-returning RPC at that value and
 * still answers 200, with nothing on the response to say rows were dropped. A larger `.limit()` does
 * not lift it, because PostgREST takes the smaller of the two. A read that expects more therefore
 * computes on a fraction of the data while the screen looks normal; the landing page summed the
 * oldest 1,000 of 6,957 transactions and showed 15% of the year's revenue. Nothing here reads or
 * assumes the setting's value: the server's own exact count is the only authority.
 */

/** One page as the Supabase query builder resolves it. `count` is present when the page asked. */
export interface RowPage<T> {
    data: T[] | null;
    error: unknown;
    count?: number | null;
}

export interface FetchAllRowsOptions<T> {
    /**
     * Builds and runs ONE page: the caller's query with `.range(from, to)` on the end, `from` and
     * `to` inclusive as PostgREST takes them. It must select with `{ count: 'exact' }` (on every
     * page is simplest; the first page is the one that is required) and must order by a key that
     * is unique across the matching rows.
     */
    page: (from: number, to: number) => PromiseLike<RowPage<T>>;
    /**
     * The unique key the page orders by, as the query spells it ("id", "transaction_at, id"),
     * quoted in every error. The helper cannot inspect the query, so naming the key is how the
     * caller states it ordered by one.
     */
    orderedBy: string;
    /** Rows asked for per request. Default 1,000; correct whatever the server's Max rows is. */
    pageSize?: number;
    /** What the rows are, for the error an admin reads ("transactions", "collections repayments"). */
    label: string;
    /**
     * The row's unique key (its primary key, as selected), when the caller can name one. With it the
     * read also throws on a row that arrives twice. A count check cannot see that: an UPDATE that
     * moves a row across a page boundary (a transaction re-dated mid-read) leaves the count as it
     * was, and the length still adds up while one row is read twice and its neighbour never. Every
     * key must be present, so a query that forgot to select the key fails rather than passing.
     * Optional, for a read whose rows genuinely carry no key. Write it AFTER `page`: the row type is
     * read from `page`, and an untyped `(r) => r.id` placed before it is typed first, as `unknown`.
     */
    keyOf?: (row: T) => string | number;
}

export const DEFAULT_PAGE_SIZE = 1000;

/**
 * The truncation guard for a single read: a short read is a wrong page, not a smaller one [R11].
 *
 * THROWS RATHER THAN RETURNING WHAT CAME BACK. A figure computed over the whole set (a sum, a
 * count) does not draw a shorter table when the server quietly cut the response; it states
 * confident totals that are all too small, with nothing on the page to say so. An error an admin
 * can read is the only honest answer to a read that did not finish.
 *
 * A MISSING COUNT IS NOT A FAILURE HERE. `count` is absent only when the read did not ask for one
 * or the server declined to compute it; with nothing to compare against, the guard lets the rows
 * through rather than blocking the page on its own bookkeeping. `fetchAllRows` is stricter, because
 * a paged read cannot know when it has finished without the count.
 *
 * `subject` IS WHAT THE ADMIN IS LOOKING AT, and the caller names it because the guard is shared:
 * "Loaded 7 of 26 premium years" on the Deposits page would send whoever read it to the wrong
 * table. `reason` is the second half of the message; the default describes a single read, the only
 * kind that had this guard before the paged read was added below.
 *
 * Moved here from `src/components/insurance-sub/premiumLedger.ts`, which re-exports it under its
 * old name and default subject, so the premium and deposit ledgers are unchanged.
 */
export function assertComplete(
    rows: readonly unknown[],
    count: number | null | undefined,
    subject: string,
    reason: string = 'the estate has outgrown a single read',
): void {
    if (count === null || count === undefined) return;
    if (rows.length >= count) return;
    throw new Error(`Loaded ${rows.length} of ${count} ${subject} — ${reason}.`);
}

/**
 * Every row a query matches, read in pages so the server's row cap cannot cut it short.
 *
 * THE FIRST PAGE'S EXACT COUNT IS THE AUTHORITY. The read stops when the rows it holds reach that
 * count, not when a page comes back short. Stopping on a short page is the usual loop and it is
 * wrong twice over: it quits after one page whenever Max rows is below the page size (500 rows
 * come back for a 1,000-row page, and the loop takes that as the end), and it cannot tell "the
 * server ran out" from "the server stopped". Each next page starts where the rows so far end, so a
 * server that returns fewer rows than were asked for costs one more request rather than a gap.
 *
 * IT THROWS INSTEAD OF RETURNING A PART, in every way a read can go wrong:
 * - a page answered with an error (a failed second page used to end some loops with page one's
 *   totals, silently);
 * - the first page carried no count, so there is nothing to prove completeness against;
 * - a later page's count disagrees with the first (rows were written or deleted under the read,
 *   which moves every offset after the change: one row is read twice and another never);
 * - the server stopped returning rows before the count was reached;
 * - more rows arrived than the count allows;
 * - with `keyOf`, a row arrived twice (a row moved across a page boundary under an unchanged count).
 *   Together with the length check, that makes the number of distinct keys equal the count.
 * The caller's error path is the screen's error state; a partial set has no honest rendering.
 *
 * WHAT NO CHECK HERE CAN SEE. Each page is its own snapshot. Between two pages, a DELETE among the
 * rows already read paired with an INSERT among those still to come keeps the count, pulls the next
 * row back across the boundary, and skips it without repeating any row, so every key is still
 * distinct. Only a single-statement read (an RPC) or a read that verifies its own boundaries sees it.
 *
 * THE CALLER'S QUERY MUST ORDER BY A UNIQUE KEY. Offset pages are only a partition of the result
 * when every page sees the same total order. Without an ORDER BY, Postgres may return the rows in
 * a different order for each request. Ordering by a column that is not unique (`transaction_at`:
 * 253 timestamps in the landing page's year carry more than one row, one carries 88) leaves the
 * order of the tied rows free, so a tie that straddles a page boundary can put the same row on both
 * pages and leave its neighbour on neither. The count check cannot see that: the length still adds
 * up. Add the primary key as the last sort column (the D-T28 `, id` tiebreaker) and name it in
 * `orderedBy`.
 */
export async function fetchAllRows<T>(opts: FetchAllRowsOptions<T>): Promise<T[]> {
    const { page, orderedBy, label, keyOf } = opts;
    const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;

    if (!Number.isInteger(pageSize) || pageSize < 1) {
        throw new Error(`${label}: page size must be a positive whole number, got ${pageSize}.`);
    }
    if (typeof orderedBy !== 'string' || orderedBy.trim() === '') {
        throw new Error(`${label}: a paged read must name the unique key its query orders by.`);
    }

    const rows: T[] = [];
    const seen = new Set<string | number>();
    let count: number | null = null;
    let pageNumber = 0;

    for (;;) {
        const from = rows.length;
        const to = from + pageSize - 1;
        pageNumber += 1;
        const result = await page(from, to);

        if (result.error) {
            throw withCause(
                new Error(
                    `${label}: page ${pageNumber} (rows ${from}–${to}, ordered by ${orderedBy}) failed: ` +
                        `${errorMessage(result.error)}. Nothing was computed from the rows read so far.`,
                ),
                result.error,
            );
        }

        if (count === null) {
            if (typeof result.count !== 'number') {
                throw new Error(
                    `${label}: the first page carried no row count, so the read cannot prove it is ` +
                        `complete. Select with { count: 'exact' }.`,
                );
            }
            count = result.count;
        } else if (typeof result.count === 'number' && result.count !== count) {
            throw new Error(
                `${label}: the row count changed from ${count} to ${result.count} during the read ` +
                    `(ordered by ${orderedBy}). Rows were written while it paged, so its pages no longer ` +
                    `line up. Reload.`,
            );
        }

        const data = result.data ?? [];
        for (const row of data) {
            if (keyOf) {
                const key = keyOf(row);
                if (key === null || key === undefined) {
                    throw new Error(
                        `${label}: row ${rows.length + 1} carries no key (ordered by ${orderedBy}), so the ` +
                            `read cannot check that no row arrived twice. Select the key column.`,
                    );
                }
                if (seen.has(key)) {
                    throw new Error(
                        `${label}: the row keyed ${String(key)} arrived twice, on page ${pageNumber} ` +
                            `(ordered by ${orderedBy}), so another row was skipped. Either the order is not ` +
                            `unique or a row moved while the read paged. Reload.`,
                    );
                }
                seen.add(key);
            }
            rows.push(row);
        }

        if (rows.length >= count) break;
        // The server stopped before the count. Asking again from the same offset would only loop;
        // the check below names the shortfall.
        if (data.length === 0) break;
    }

    if (rows.length > count) {
        throw new Error(
            `Loaded ${rows.length} of ${count} ${label} — more rows than the server counted ` +
                `(ordered by ${orderedBy}); rows were written while it paged. Reload.`,
        );
    }
    assertComplete(
        rows,
        count,
        label,
        `the server stopped returning rows before its own count (ordered by ${orderedBy}, ` +
            `${pageNumber} ${pageNumber === 1 ? 'page' : 'pages'} of ${pageSize})`,
    );
    return rows;
}

function errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message: unknown }).message);
    }
    return String(error);
}

/** `new Error(message, { cause })` without the ES2022 lib this project does not compile against. */
function withCause(error: Error, cause: unknown): Error {
    (error as Error & { cause?: unknown }).cause = cause;
    return error;
}
